import { onBeforeUnmount, shallowRef, type Ref } from 'vue'

/**
 * 基础地图配置选项
 */
interface UseTMapBaseOptions {
  /** 地图中心点坐标 [经度, 纬度] */
  center: LngLat
  /** 地图缩放级别，默认为18 */
  zoom?: number
}

/**
 * 天地图基础功能 Hook
 * 提供地图初始化、控件添加等基础功能
 * @param mapRef - 地图容器的 DOM 元素引用
 * @param options - 地图配置选项
 * @returns 地图实例和相关方法
 */
export function useTMapBase(mapRef: Ref<HTMLElement | undefined>, options: UseTMapBaseOptions) {
  const T = window.T

  /** 地图实例 */
  const Map = shallowRef<any>(null)

  /**
   * 添加默认地图控件
   * 包括：地图类型切换、缩放平移、比例尺
   * @param mapIns - 地图实例
   */
  const addDefaultControls = (mapIns: any) => {
    // 添加地图类型切换控件
    const ctrl = new T.Control.MapType()
    mapIns.addControl(ctrl)

    // 添加缩放平移控件
    const zoom = new T.Control.Zoom()
    mapIns.addControl(zoom)

    // 添加比例尺控件
    const scale = new T.Control.Scale()
    mapIns.addControl(scale)
  }

  /**
   * 初始化地图实例
   * 创建地图并设置中心点、缩放级别及默认控件
   *
   * @returns 地图实例，如果容器不存在则返回 null
   */
  const initMap = () => {
    if (!mapRef.value) return null

    const mapIns = new T.Map(mapRef.value)
    const zoom = options.zoom ?? 18
    mapIns.centerAndZoom(new T.LngLat(...options.center), zoom)

    addDefaultControls(mapIns)

    Map.value = mapIns
    return mapIns
  }

  /**
   * 销毁地图实例
   * 清空地图引用，释放资源
   */
  const destroyMap = () => {
    Map.value = null
  }

  onBeforeUnmount(destroyMap)

  return {
    Map,
    initMap,
    destroyMap
  }
}
